import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  Chip,
  Tooltip,
  Link as MuiLink,
  LinearProgress,
  Fade,
  alpha
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridFilterModel, GridSortModel } from "@mui/x-data-grid";
import { BarChart } from "@mui/x-charts/BarChart";
import { Stack, MessageBar, MessageBarType, SearchBox, IContextualMenuItem, mergeStyles } from "@fluentui/react";
import {
  CheckmarkCircle24Regular,
  Clock24Regular,
  ArrowTrendingLines24Regular,
  DocumentBulletList24Regular,
  Info16Regular,
  Warning24Regular,
  ArrowClockwise16Regular
} from "@fluentui/react-icons";
import { useGetDCRAutomationQuery } from "../../../slices/CloseTheLoop.slice";
import { IDCRAutomationRequest, IDCRConversionByOffering, IFailureOverlapAnalysis } from "../../../interfaces/CloseTheLoop.interfaces";
import { logPageView } from "../../../helper/AppInsightsHelper";
import { formatDisplayDate } from "../../../Utils/js-common";
import { CustomDataGridToolbar } from "../../../SharedComponents/CustomDataGrid/CustomDataGridToolbar";
import { CustomFilterPill } from "../../../SharedComponents/CustomFilterPill";
import { IFilterPillStyles } from "@m365-admin/in-page-filter";
import { CTLExploreDataButton } from "../utils/CTLExploreDataButton";
import { CTL_DCR_AUTOMATION_QUERY, generateDCRAutomationQuery } from "../utils/ctlKustoQueries";
import { CTLInfoTooltip } from "../utils/CTLInfoTooltip";

// Security 360 Theme - Soft, professional colors
const THEME = {
  // Primary blues
  primary: "#0078D4",
  primaryLight: "#E6F2FA",
  primaryMedium: "#CCE4F7",
  primaryDark: "#005A9E",

  // Backgrounds
  background: "#F8FAFC",
  surface: "#FFFFFF",
  cardBg: "#FAFBFC",

  // Text
  textPrimary: "#1A1A2E",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",

  // Borders
  border: "#E2E8F0",
  borderLight: "#F1F5F9",

  // Status colors - softer versions
  success: "#059669",
  successLight: "#D1FAE5",
  successMedium: "#A7F3D0",

  warning: "#D97706",
  warningLight: "#FEF3C7",
  warningMedium: "#FDE68A",

  error: "#DC2626",
  errorLight: "#FEE2E2",
  errorMedium: "#FECACA",

  // Purple for delayed/retry
  purple: "#7C3AED",
  purpleLight: "#EDE9FE",
  purpleMedium: "#DDD6FE"
};

// Filter pill styles matching the theme
const FILTER_PILL_STYLES: Partial<IFilterPillStyles> = {
  root: {
    margin: "4px",
    flex: "0 1 auto",
    minWidth: 0
  },
  subComponentStyles: {
    contextualMenu: {
      container: { height: "auto", transition: "min-width 1s ease-in-out" },
      list: { maxHeight: "calc(80vh / 2)", overflow: "auto", "& .ms-ContextualMenu-link": { height: "auto" } }
    },
    callout: { calloutMain: {} }
  }
};

// Helper to convert options to menu items for FilterPill
const mapToMenuItems = (options: string[]): IContextualMenuItem[] =>
  options.map((value) => ({ key: value, text: value }));

// Softer category colors
const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  "Successfully Created (First Attempt)": { bg: THEME.successLight, text: THEME.success, border: THEME.successMedium },
  "Successfully Created (After Retry)": { bg: THEME.purpleLight, text: THEME.purple, border: THEME.purpleMedium },
  "DCR Pending Triage": { bg: THEME.warningLight, text: THEME.warning, border: THEME.warningMedium },
  "Not Converted to DCR": { bg: THEME.errorLight, text: THEME.error, border: THEME.errorMedium }
};

// Softer conversion colors
const CONVERSION_COLORS = {
  immediate: "#10B981", // Emerald
  delayed: "#8B5CF6", // Violet
  pending: "#F59E0B", // Amber
  never: "#EF4444" // Red
};

/**
 * Summary Card Component - Matches mockup design with left accent
 */
interface SummaryCardProps {
  title: string;
  value: number;
  subtitle?: string;
  accentColor: string;
  isLoading?: boolean;
  /** Business-friendly explanation of the metric, rendered as an info icon next to the title */
  tooltip?: React.ReactNode;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, subtitle, accentColor, isLoading, tooltip }) => {
  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "12px",
          border: `1px solid ${THEME.border}`,
          background: `linear-gradient(135deg, ${THEME.surface} 0%, ${THEME.cardBg} 100%)`,
          height: 120,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        }}
      >
        <Skeleton variant="text" width={60} height={40} sx={{ borderRadius: "6px" }} />
        <Skeleton variant="text" width={140} height={18} sx={{ borderRadius: "4px" }} />
        <Skeleton variant="text" width={100} height={14} sx={{ borderRadius: "4px" }} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        pl: 3,
        borderRadius: "12px",
        border: `1px solid ${THEME.border}`,
        background: `linear-gradient(135deg, ${THEME.surface} 0%, ${alpha(accentColor, 0.03)} 100%)`,
        height: 120,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: alpha(accentColor, 0.5),
          background: `linear-gradient(135deg, ${THEME.surface} 0%, ${alpha(accentColor, 0.08)} 100%)`,
          boxShadow: `0 8px 24px ${alpha(accentColor, 0.15)}, 0 4px 8px ${alpha(accentColor, 0.1)}`,
          "& .summary-value": {
            color: accentColor
          },
          "&::before": {
            width: "5px",
            boxShadow: `2px 0 12px ${alpha(accentColor, 0.4)}`
          },
          "&::after": {
            opacity: 1
          }
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: "4px",
          backgroundColor: accentColor,
          borderRadius: "4px 0 0 4px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "40%",
          background: `linear-gradient(90deg, transparent 0%, ${alpha(accentColor, 0.05)} 100%)`,
          opacity: 0,
          transition: "opacity 0.3s ease",
          pointerEvents: "none"
        }
      }}
    >
      <Typography
        className="summary-value"
        sx={{
          fontSize: "34px",
          fontWeight: 700,
          color: THEME.textPrimary,
          lineHeight: 1,
          mb: 0.75,
          letterSpacing: "-0.5px",
          transition: "color 0.3s ease"
        }}
      >
        {value.toLocaleString()}
      </Typography>
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 600,
          color: THEME.textSecondary,
          mb: 0.5,
          letterSpacing: "0.2px",
          display: "flex",
          alignItems: "center",
          gap: 0.75
        }}
      >
        {title}
        {tooltip}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            fontSize: "11px",
            color: THEME.textMuted,
            fontWeight: 500,
            letterSpacing: "0.1px"
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
};

/**
 * Chart Card Wrapper - Consistent styling for all charts
 */
interface ChartCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  subtitle?: string;
  action?: React.ReactNode;
  minHeight?: number;
  kustoQuery?: string;
  /** Business-friendly explanation of the visual, rendered as an info icon next to the title */
  tooltip?: React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, icon, children, subtitle, action, minHeight = 280, kustoQuery, tooltip }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "12px",
      border: `1px solid ${THEME.border}`,
      backgroundColor: THEME.surface,
      height: minHeight,
      display: "flex",
      flexDirection: "column"
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 1.5,
        pb: 1.5,
        borderBottom: `1px solid ${THEME.borderLight}`
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            p: 0.75,
            borderRadius: "8px",
            backgroundColor: THEME.primaryLight,
            display: "flex"
          }}
        >
          {icon}
        </Box>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: THEME.textPrimary }}>{title}</Typography>
            {tooltip}
          </Box>
          {subtitle && <Typography sx={{ fontSize: "11px", color: THEME.textMuted }}>{subtitle}</Typography>}
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {action}
      </Box>
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>{children}</Box>
    {kustoQuery && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          minHeight: 45,
          borderTop: `2px solid ${THEME.borderLight}`,
          mt: 1,
          pt: 1
        }}
      >
        <CTLExploreDataButton
          query={kustoQuery}
          chartTitle={title}
        />
      </Box>
    )}
  </Paper>
);

/**
 * Failure Reasons Chart - Redesigned with clickable filters for all elements
 * - Summary cards (Single Issue / Multiple Issues) are clickable
 * - Individual reasons show "only this" vs "has this" breakdown
 * - Exact combinations are clickable to filter
 * 
 * Filter key conventions:
 * - issueCount:single - items with exactly 1 failure reason
 * - issueCount:multiple - items with 2+ failure reasons  
 * - hasReason:ReasonName - items that have this reason (may have others)
 * - combo:Reason1+Reason2+... - items with this exact combination
 */
interface FailureReasonsChartProps {
  data: Array<{ reason: string; count: number; percentage: number; color: string; filterKey: string }>;
  isLoading: boolean;
  selectedFilterKey?: string;
  onReasonClick?: (filterKey: string | undefined) => void;
  filteredCount?: number;
  /** Count of items with multiple failure reasons (helps explain overlap) */
  itemsWithMultipleIssues?: number;
  /** Detailed overlap analysis */
  failureOverlapAnalysis?: IFailureOverlapAnalysis;
}

// Short labels for display
const SHORT_LABELS: Record<string, string> = {
  "Unmapped Product": "Unmapped",
  "Missing TPID": "No TPID",
  "Invalid UAT State": "Bad State",
  "Missing Parent Feature": "No Feature"
};

// Color map for reasons
const REASON_COLORS: Record<string, string> = {
  "Unmapped Product": "#F87171",
  "Missing TPID": "#FB923C",
  "Invalid UAT State": "#A78BFA",
  "Missing Parent Feature": "#60A5FA"
};

// Helper to parse filter key and get display label
const getFilterLabel = (filterKey: string): string => {
  if (filterKey.startsWith("issueCount:")) {
    const type = filterKey.split(":")[1];
    return type === "single" ? "Single Issue Items" : "Multiple Issue Items";
  }
  if (filterKey.startsWith("hasReason:")) {
    const reason = filterKey.split(":")[1];
    return `Has: ${reason}`;
  }
  if (filterKey.startsWith("combo:")) {
    const reasons = filterKey.split(":")[1].split("+");
    return reasons.map(r => SHORT_LABELS[r] || r).join(" + ");
  }
  return filterKey;
};

const FailureReasonsChart: React.FC<FailureReasonsChartProps> = ({
  data,
  isLoading,
  selectedFilterKey,
  onReasonClick,
  filteredCount,
  itemsWithMultipleIssues = 0,
  failureOverlapAnalysis
}) => {
  const handleFilterClick = useCallback(
    (filterKey: string) => {
      if (!onReasonClick) return;
      onReasonClick(selectedFilterKey === filterKey ? undefined : filterKey);
    },
    [onReasonClick, selectedFilterKey]
  );

  if (isLoading) {
    return <Skeleton variant="rectangular" height={480} sx={{ borderRadius: "12px" }} />;
  }

  // Use overlap analysis if available
  const overlapData = failureOverlapAnalysis?.overlapByReason || [];
  const combinations = failureOverlapAnalysis?.combinations || [];
  const totalFailed = failureOverlapAnalysis?.totalFailedItems || 0;
  const singleIssue = failureOverlapAnalysis?.itemsWithSingleIssue || 0;
  const multipleIssues = failureOverlapAnalysis?.itemsWithMultipleIssues || itemsWithMultipleIssues;

  // Filter to show only the 4 main failure reasons
  const mainReasons = overlapData.filter(r =>
    ["Unmapped Product", "Missing TPID", "Invalid UAT State", "Missing Parent Feature"].includes(r.reason)
  );

  // Get all single-issue items breakdown (items with exactly 1 issue)
  const singleIssueCombos = combinations.filter(c => c.reasons.length === 1);
  // Get all multi-issue combinations
  const multiIssueCombos = combinations.filter(c => c.reasons.length > 1);

  // Check if current filter is active
  const isFilterActive = (key: string) => selectedFilterKey === key;

  return (
    <ChartCard
      title="Failure Reasons Analysis"
      icon={<Warning24Regular style={{ color: THEME.primary, fontSize: 16 }} />}
      subtitle="Click any element to filter the table below"
      minHeight={480}
      tooltip={
        <CTLInfoTooltip
          title="Failure Reasons Analysis"
          description="Why customer feedback failed to become a formal request. It separates items blocked by a single problem from items with several problems at once, so you know which fixes will unblock the most feedback."
          howItsCalculated="Each blocked item is checked against four common blockers — the product is not one we support, the customer account number is missing, the feedback is in a state we cannot act on, or it is not linked to a parent feature. We then count how many items hit each blocker, and which blockers tend to occur together."
          note="Items with more than one blocker appear in each relevant blocker count, so those counts add up to more than the total."
        />
      }
      kustoQuery={`${CTL_DCR_AUTOMATION_QUERY}
| where not(DCRPresent)
| extend HasUnmapped = not(IsSupportedProduct), HasMissingTPID = isempty(TPID), HasInvalidState = not(HasValidStatus), HasMissingFeature = not(IsLinkedToValidFeature)
| extend IssueCount = toint(HasUnmapped) + toint(HasMissingTPID) + toint(HasInvalidState) + toint(HasMissingFeature)
| summarize TotalFailed = dcount(WorkItemId), SingleIssue = dcountif(WorkItemId, IssueCount == 1), MultipleIssues = dcountif(WorkItemId, IssueCount > 1)`}
    >
      {/* Active Filter Banner */}
      {selectedFilterKey && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
            p: 1,
            borderRadius: "10px",
            background: `linear-gradient(135deg, ${alpha(THEME.success, 0.12)} 0%, ${alpha(THEME.success, 0.06)} 100%)`,
            border: `1px solid ${alpha(THEME.success, 0.3)}`
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "6px", backgroundColor: THEME.success, color: "white", fontSize: "12px" }}>
              ✓
            </Box>
            <Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: THEME.success }}>
                Filtering: {getFilterLabel(selectedFilterKey)}
              </Typography>
              <Typography sx={{ fontSize: "10px", color: THEME.textSecondary }}>
                {filteredCount?.toLocaleString() || 0} items shown in table below
              </Typography>
            </Box>
          </Box>
          <Box
            component="button"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onReasonClick?.(undefined); }}
            sx={{ px: 1, py: 0.5, border: `1px solid ${THEME.success}`, borderRadius: "4px", backgroundColor: "white", color: THEME.success, fontSize: "10px", fontWeight: 600, cursor: "pointer", "&:hover": { backgroundColor: THEME.success, color: "white" } }}
          >
            × Clear
          </Box>
        </Box>
      )}

      {mainReasons.length === 0 ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: 1 }}>
          <CheckmarkCircle24Regular style={{ color: THEME.success, fontSize: 40 }} />
          <Typography sx={{ color: THEME.textMuted }}>No failures recorded</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* Summary Row - Clickable cards */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
            {/* Total Failed - Not clickable */}
            <Box sx={{ textAlign: "center", p: 1, borderRadius: "8px", backgroundColor: alpha(THEME.error, 0.08) }}>
              <Typography sx={{ fontSize: "20px", fontWeight: 700, color: THEME.error }}>{totalFailed.toLocaleString()}</Typography>
              <Typography sx={{ fontSize: "9px", color: THEME.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Failed</Typography>
            </Box>
            {/* Single Issue - Clickable */}
            <Tooltip title="Click to see items with exactly ONE failure reason">
              <Box
                onClick={() => handleFilterClick("issueCount:single")}
                sx={{
                  textAlign: "center", p: 1, borderRadius: "8px", cursor: "pointer",
                  backgroundColor: isFilterActive("issueCount:single") ? alpha(THEME.warning, 0.2) : alpha(THEME.warning, 0.08),
                  border: isFilterActive("issueCount:single") ? `2px solid ${THEME.warning}` : "2px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": { backgroundColor: alpha(THEME.warning, 0.15), borderColor: THEME.warning }
                }}
              >
                <Typography sx={{ fontSize: "20px", fontWeight: 700, color: THEME.warning }}>{singleIssue.toLocaleString()}</Typography>
                <Typography sx={{ fontSize: "9px", color: THEME.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>Single Issue ▼</Typography>
              </Box>
            </Tooltip>
            {/* Multiple Issues - Clickable */}
            <Tooltip title="Click to see items with TWO OR MORE failure reasons">
              <Box
                onClick={() => handleFilterClick("issueCount:multiple")}
                sx={{
                  textAlign: "center", p: 1, borderRadius: "8px", cursor: "pointer",
                  backgroundColor: isFilterActive("issueCount:multiple") ? alpha(THEME.purple, 0.2) : alpha(THEME.purple, 0.08),
                  border: isFilterActive("issueCount:multiple") ? `2px solid ${THEME.purple}` : "2px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": { backgroundColor: alpha(THEME.purple, 0.15), borderColor: THEME.purple }
                }}
              >
                <Typography sx={{ fontSize: "20px", fontWeight: 700, color: THEME.purple }}>{multipleIssues.toLocaleString()}</Typography>
                <Typography sx={{ fontSize: "9px", color: THEME.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>Multiple Issues ▼</Typography>
              </Box>
            </Tooltip>
          </Box>

          {/* Individual Reason Breakdown - Shows total with each reason */}
          <Box sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: THEME.textSecondary, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              By Failure Reason (click to filter)
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 0.75 }}>
              {mainReasons.map((item) => {
                const filterKey = `hasReason:${item.reason}`;
                const isActive = isFilterActive(filterKey);
                return (
                  <Tooltip key={item.reason} title={`${item.totalWithIssue} items have "${item.reason}" (${item.onlyThisIssue} only this, ${item.withOtherIssues} with others)`}>
                    <Box
                      onClick={() => handleFilterClick(filterKey)}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        p: 1, borderRadius: "8px", cursor: "pointer",
                        backgroundColor: isActive ? alpha(item.color, 0.15) : "white",
                        border: isActive ? `2px solid ${item.color}` : `1px solid ${THEME.border}`,
                        transition: "all 0.2s ease",
                        "&:hover": { borderColor: item.color, boxShadow: `0 2px 8px ${alpha(item.color, 0.2)}` }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: item.color }} />
                        <Box>
                          <Typography sx={{ fontSize: "12px", fontWeight: isActive ? 600 : 500, color: isActive ? item.color : THEME.textPrimary }}>
                            {item.reason}
                          </Typography>
                          <Typography sx={{ fontSize: "9px", color: THEME.textMuted }}>
                            {item.onlyThisIssue} only • {item.withOtherIssues} +others
                          </Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: "16px", fontWeight: 700, color: item.color }}>{item.totalWithIssue}</Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>

          {/* Exact Combinations - Clickable to filter */}
          <Box sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: THEME.textSecondary, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              📋 Exact Combinations (click to see those items)
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxHeight: 180, overflowY: "auto" }}>
              {/* Single issue items first */}
              {singleIssueCombos.map((combo, idx) => {
                const filterKey = `combo:${combo.reasons.join("+")}`;
                const isActive = isFilterActive(filterKey);
                const reason = combo.reasons[0];
                const color = REASON_COLORS[reason] || THEME.textSecondary;
                return (
                  <Box
                    key={`single-${idx}`}
                    onClick={() => handleFilterClick(filterKey)}
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      p: 0.75, borderRadius: "6px", cursor: "pointer",
                      backgroundColor: isActive ? alpha(color, 0.15) : alpha(THEME.warning, 0.04),
                      border: isActive ? `2px solid ${color}` : `1px solid ${alpha(THEME.warning, 0.15)}`,
                      transition: "all 0.15s ease",
                      "&:hover": { backgroundColor: alpha(color, 0.1), borderColor: color }
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography sx={{ fontSize: "9px", color: THEME.warning, fontWeight: 600, mr: 0.5 }}>1×</Typography>
                      <Chip
                        label={SHORT_LABELS[reason] || reason}
                        size="small"
                        sx={{
                          height: "18px", fontSize: "9px", fontWeight: 600,
                          backgroundColor: alpha(color, 0.15), color: color,
                          border: `1px solid ${alpha(color, 0.3)}`,
                          "& .MuiChip-label": { px: 0.75 }
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: color }}>{combo.count}</Typography>
                      <Typography sx={{ fontSize: "9px", color: THEME.textMuted }}>({combo.percentage}%)</Typography>
                    </Box>
                  </Box>
                );
              })}
              {/* Multi-issue combinations */}
              {multiIssueCombos.map((combo, idx) => {
                const filterKey = `combo:${combo.reasons.join("+")}`;
                const isActive = isFilterActive(filterKey);
                return (
                  <Box
                    key={`multi-${idx}`}
                    onClick={() => handleFilterClick(filterKey)}
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      p: 0.75, borderRadius: "6px", cursor: "pointer",
                      backgroundColor: isActive ? alpha(THEME.purple, 0.15) : alpha(THEME.purple, 0.04),
                      border: isActive ? `2px solid ${THEME.purple}` : `1px solid ${alpha(THEME.purple, 0.12)}`,
                      transition: "all 0.15s ease",
                      "&:hover": { backgroundColor: alpha(THEME.purple, 0.1), borderColor: THEME.purple }
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "9px", color: THEME.purple, fontWeight: 600, mr: 0.5 }}>{combo.reasons.length}×</Typography>
                      {combo.reasons.map((reason, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <Typography sx={{ fontSize: "9px", color: THEME.textMuted }}>+</Typography>}
                          <Chip
                            label={SHORT_LABELS[reason] || reason}
                            size="small"
                            sx={{
                              height: "18px", fontSize: "9px", fontWeight: 600,
                              backgroundColor: alpha(REASON_COLORS[reason] || THEME.purple, 0.15),
                              color: REASON_COLORS[reason] || THEME.purple,
                              border: `1px solid ${alpha(REASON_COLORS[reason] || THEME.purple, 0.3)}`,
                              "& .MuiChip-label": { px: 0.75 }
                            }}
                          />
                        </React.Fragment>
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: THEME.purple }}>{combo.count}</Typography>
                      <Typography sx={{ fontSize: "9px", color: THEME.textMuted }}>({combo.percentage}%)</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </ChartCard>
  );
};

/**
 * Recovery Time Chart
 */
interface RecoveryTimeChartProps {
  data: Array<{ bucket: string; count: number; order: number }>;
  isLoading: boolean;
}

const RecoveryTimeChart: React.FC<RecoveryTimeChartProps> = ({ data, isLoading }) => {
  const bucketLabels = useMemo(() => data.map((d) => d.bucket), [data]);
  const countValues = useMemo(() => data.map((d) => d.count), [data]);

  if (isLoading) {
    return <Skeleton variant="rectangular" height={370} sx={{ borderRadius: "12px" }} />;
  }

  return (
    <ChartCard
      title="Recovery Time Distribution"
      icon={<Clock24Regular style={{ color: THEME.primary, fontSize: 16 }} />}
      subtitle="Days from tag to DCR creation (retry cases)"
      minHeight={480}
      tooltip={
        <CTLInfoTooltip
          title="Recovery Time Distribution"
          description="When automation does not work first time, how long does the customer end up waiting? This shows the size of the delay for every request that needed a retry."
          howItsCalculated="We take only the requests that took longer than 3 days to create, work out the days between the feedback being flagged and the request finally appearing, and group them into time bands (4–7 days, 8–14 days, and so on)."
          note="Requests created cleanly within 3 days are not shown here — this chart is about delays only."
        />
      }
      kustoQuery={`${CTL_DCR_AUTOMATION_QUERY}
| where DCRPresent and DaysToCreateDCR > 3
| extend Bucket = case(
    DaysToCreateDCR <= 7, "4-7 days",
    DaysToCreateDCR <= 14, "8-14 days",
    DaysToCreateDCR <= 30, "15-30 days",
    "30+ days")
| summarize Count = count() by Bucket`}
    >
      {data.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            flexDirection: "column",
            gap: 1
          }}
        >
          <Typography sx={{ color: THEME.textMuted }}>No recovery data available</Typography>
        </Box>
      ) : (
        <BarChart
          xAxis={[
            {
              scaleType: "band",
              data: bucketLabels,
              tickLabelStyle: { fontSize: 10, fill: THEME.textSecondary }
            }
          ]}
          yAxis={[
            {
              tickLabelStyle: { fontSize: 10, fill: THEME.textSecondary }
            }
          ]}
          series={[
            {
              data: countValues,
              color: "#8B5CF6"
            }
          ]}
          height={280}
          margin={{ top: 15, right: 15, bottom: 25, left: 40 }}
          slotProps={{ legend: { hidden: true } }}
          sx={{
            "& .MuiBarElement-root": {
              rx: 3,
              ry: 3
            }
          }}
        />
      )}
    </ChartCard>
  );
};

/**
 * Conversion by Offering Chart
 */
interface ConversionByOfferingChartProps {
  data: IDCRConversionByOffering[];
  selectedOfferings: string[];
  isLoading: boolean;
}

const ConversionByOfferingChart: React.FC<ConversionByOfferingChartProps> = ({
  data,
  selectedOfferings,
  isLoading
}) => {
  const filteredData =
    selectedOfferings.length === 0 ? data : data.filter((d) => selectedOfferings.includes(d.offeringName));

  const chartData = useMemo(() => {
    return [...filteredData].sort((a, b) => b.total - a.total).slice(0, 8);
  }, [filteredData]);

  const totalDCRs = filteredData.reduce((sum, d) => sum + d.total, 0);
  const maxTotal = Math.max(...chartData.map((d) => d.total), 1);

  if (isLoading) {
    return <Skeleton variant="rectangular" height={340} sx={{ borderRadius: "12px" }} />;
  }

  return (
    <ChartCard
      title="Conversion by Offering"
      icon={<ArrowTrendingLines24Regular style={{ color: THEME.primary, fontSize: 16 }} />}
      tooltip={
        <CTLInfoTooltip
          title="Conversion by Offering"
          description="Which products have a healthy intake pipeline and which do not. For every offering it shows how much feedback converted straight away, how much was delayed, how much is still waiting, and how much never converted at all."
          howItsCalculated="Feedback for each offering is sorted into four outcomes: Immediate (created first time), Delayed (needed a retry), Pending (created but still awaiting triage), and Never (no request exists). The bar shows the mix as a proportion of that offering's feedback."
          note="Offerings with a large 'Never' band are the ones where customer voice is being lost most often."
        />
      }
      action={
        <Chip
          label={`${totalDCRs.toLocaleString()} total`}
          size="small"
          sx={{
            backgroundColor: THEME.primaryLight,
            color: THEME.primary,
            fontWeight: 600,
            fontSize: "11px",
            height: 24
          }}
        />
      }
      minHeight={400}
      kustoQuery={`${CTL_DCR_AUTOMATION_QUERY}
| summarize 
    FirstAttempt = countif(DCRPresent and DaysToCreateDCR <= 3),
    Retry = countif(DCRPresent and DaysToCreateDCR > 3),
      Pending = countif(not(DCRPresent) and TriageStatus == "CXE Triage Needed"),
      Failed = countif(not(DCRPresent) and TriageStatus != "CXE Triage Needed")
    by OfferingName
| extend Total = FirstAttempt + Retry + Pending + Failed
| sort by Total desc
| take 20`}
    >
      {chartData.length === 0 ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <Typography sx={{ color: THEME.textMuted }}>No data available</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <Stack tokens={{ childrenGap: 8 }}>
              {chartData.map((item) => (
                <Box key={item.offeringName}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Tooltip title={item.offeringName} arrow>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: THEME.textPrimary,
                          fontWeight: 500,
                          maxWidth: 120,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {item.offeringName}
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.textPrimary }}>
                      {item.total}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      height: 16,
                      borderRadius: 4,
                      overflow: "hidden",
                      backgroundColor: THEME.borderLight
                    }}
                  >
                    {item.immediate > 0 && (
                      <Tooltip title={`Created (First Attempt): ${item.immediate}`} arrow>
                        <Box
                          sx={{
                            width: `${(item.immediate / maxTotal) * 100}%`,
                            backgroundColor: CONVERSION_COLORS.immediate,
                            transition: "width 0.3s ease"
                          }}
                        />
                      </Tooltip>
                    )}
                    {item.delayed > 0 && (
                      <Tooltip title={`Created (After Retry): ${item.delayed}`} arrow>
                        <Box
                          sx={{
                            width: `${(item.delayed / maxTotal) * 100}%`,
                            backgroundColor: CONVERSION_COLORS.delayed
                          }}
                        />
                      </Tooltip>
                    )}
                    {item.pending > 0 && (
                      <Tooltip title={`Pending Triage: ${item.pending}`} arrow>
                        <Box
                          sx={{
                            width: `${(item.pending / maxTotal) * 100}%`,
                            backgroundColor: CONVERSION_COLORS.pending
                          }}
                        />
                      </Tooltip>
                    )}
                    {item.never > 0 && (
                      <Tooltip title={`Not Converted: ${item.never}`} arrow>
                        <Box
                          sx={{
                            width: `${(item.never / maxTotal) * 100}%`,
                            backgroundColor: CONVERSION_COLORS.never
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Legend */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              pt: 1.5,
              mt: 1.5,
              borderTop: `1px solid ${THEME.borderLight}`,
              flexWrap: "wrap"
            }}
          >
            {[
              { label: "Immediate", color: CONVERSION_COLORS.immediate },
              { label: "Delayed", color: CONVERSION_COLORS.delayed },
              { label: "Pending", color: CONVERSION_COLORS.pending },
              { label: "Never", color: CONVERSION_COLORS.never }
            ].map((item) => (
              <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 3, backgroundColor: item.color }} />
                <Typography sx={{ fontSize: "11px", color: THEME.textSecondary }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </ChartCard>
  );
};

/**
 * DCR Creation by Product Monthly Chart
 */
interface DCRCreationByProductChartProps {
  data: Array<{ yearMonth: string; offeringName: string; azServiceName: string; dcrCount: number }>;
  selectedOfferings: string[];
  isLoading: boolean;
}

const DCRCreationByProductChart: React.FC<DCRCreationByProductChartProps> = ({
  data,
  selectedOfferings,
  isLoading
}) => {
  const filteredData =
    selectedOfferings.length === 0 ? data : data.filter((d) => selectedOfferings.includes(d.offeringName));

  const { monthLabels, seriesData } = useMemo(() => {
    const monthMap = new Map<string, Record<string, number>>();
    const services = new Set<string>();

    filteredData.forEach((d) => {
      if (!monthMap.has(d.yearMonth)) {
        monthMap.set(d.yearMonth, {});
      }
      const monthData = monthMap.get(d.yearMonth)!;
      monthData[d.azServiceName] = (monthData[d.azServiceName] || 0) + d.dcrCount;
      services.add(d.azServiceName);
    });

    const sortedMonths = Array.from(monthMap.keys()).sort().slice(-12);
    // Keep full YYYY-MM format for x-axis
    const labels = sortedMonths;

    // Softer color palette
    const colors = [
      "#3B82F6",
      "#10B981",
      "#8B5CF6",
      "#F59E0B",
      "#06B6D4",
      "#EC4899",
      "#6366F1",
      "#14B8A6",
      "#F97316",
      "#84CC16"
    ];

    // Limit to top 5 services to avoid legend overflow
    const serviceTotals = Array.from(services)
      .map((service) => ({
        service,
        total: sortedMonths.reduce((sum, month) => sum + (monthMap.get(month)?.[service] || 0), 0)
      }))
      .sort((a, b) => b.total - a.total);

    const topServices = serviceTotals.slice(0, 5).map((s) => s.service);

    const series = topServices.map((service, index) => ({
      data: sortedMonths.map((month) => monthMap.get(month)?.[service] || 0),
      label: service.length > 20 ? service.substring(0, 18) + "..." : service,
      stack: "total",
      color: colors[index % colors.length]
    }));

    return { monthLabels: labels, seriesData: series };
  }, [filteredData]);

  const totalDCRs = filteredData.reduce((sum, d) => sum + d.dcrCount, 0);

  if (isLoading) {
    return <Skeleton variant="rectangular" height={400} sx={{ borderRadius: "12px" }} />;
  }

  return (
    <ChartCard
      title="Monthly UAT → CRM DCR Creation by Product"
      subtitle={selectedOfferings.length > 0 ? `Filtered by ${selectedOfferings.length} offering(s)` : undefined}
      icon={<DocumentBulletList24Regular style={{ color: THEME.primary, fontSize: 16 }} />}
      tooltip={
        <CTLInfoTooltip
          title="Monthly UAT → CRM DCR Creation by Product"
          description="The throughput trend of the intake pipeline — how many customer requests were successfully raised each month, and which products they were for. It shows whether intake volume is growing, steady or dropping off."
          howItsCalculated="We take every request that was successfully created, group it by the month it was created and by the product it relates to, and count them. Each colour band in a month is one product."
          note="Only successfully created requests are counted; feedback that never converted does not appear here."
        />
      }
      action={
        <Chip
          label={`${totalDCRs.toLocaleString()} DCRs`}
          size="small"
          sx={{
            backgroundColor: THEME.primaryLight,
            color: THEME.primary,
            fontWeight: 600,
            fontSize: "11px",
            height: 24
          }}
        />
      }
      minHeight={400}
      kustoQuery={`${CTL_DCR_AUTOMATION_QUERY}
| where DCRPresent
    | extend YearMonth = format_datetime(DCRCreatedDate, "yyyy-MM")
    | summarize DCRCount = count() by YearMonth, AZServiceName
| sort by YearMonth asc`}
    >
      {monthLabels.length === 0 ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <Typography sx={{ color: THEME.textMuted }}>No data available</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ flex: 1 }}>
            <BarChart
              xAxis={[
                {
                  scaleType: "band",
                  data: monthLabels,
                  tickLabelStyle: { fontSize: 10, fill: THEME.textSecondary }
                }
              ]}
              yAxis={[
                {
                  label: "DCRs Created",
                  labelStyle: { fontSize: 10, fill: THEME.textSecondary },
                  tickLabelStyle: { fontSize: 10, fill: THEME.textSecondary }
                }
              ]}
              series={seriesData}
              height={300}
              margin={{ top: 20, right: 20, bottom: 80, left: 50 }}
              slotProps={{
                legend: {
                  direction: "row",
                  position: { vertical: "bottom", horizontal: "middle" },
                  padding: { top: 20 },
                  itemMarkWidth: 10,
                  itemMarkHeight: 10,
                  labelStyle: { fontSize: 10, fill: THEME.textSecondary },
                  itemGap: 15
                }
              }}
              sx={{
                "& .MuiBarElement-root": {
                  rx: 2,
                  ry: 2
                }
              }}
            />
          </Box>
        </Box>
      )}
    </ChartCard>
  );
};

/**
 * Main IntakeToTriage Component
 */
const IntakeToTriage: React.FC = () => {
  const [selectedOfferings, setSelectedOfferings] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedFailureFilterKey, setSelectedFailureFilterKey] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "tagAppliedDate", sort: "desc" }]);

  const queryParams = useMemo<IDCRAutomationRequest | undefined>(() => {
    const params: IDCRAutomationRequest = {};
    if (selectedOfferings.length > 0) {
      params.offerings = selectedOfferings.join(",");
    }
    if (selectedProducts.length > 0) {
      params.azureServices = selectedProducts.join(",");
    }
    if (searchTerm.trim()) {
      params.searchTerm = searchTerm.trim();
    }
    return Object.keys(params).length > 0 ? params : undefined;
  }, [selectedOfferings, selectedProducts, searchTerm]);

  const { data, isLoading, error, isFetching } = useGetDCRAutomationQuery(queryParams, {
    refetchOnMountOrArgChange: 300,
    refetchOnFocus: false
  });

  useEffect(() => {
    logPageView("Close The Loop - Intake to Triage Tab", window.location.href);
  }, []);

  const filteredRows = useMemo(() => {
    if (!data?.reconciliationRows) return [];
    let rows = data.reconciliationRows;

    // Filter by category
    if (selectedCategories.length > 0) {
      rows = rows.filter((row) => selectedCategories.includes(row.category));
    }

    // Filter by product / Azure service name
    if (selectedProducts.length > 0) {
      rows = rows.filter((row) => selectedProducts.includes(row.azServiceName));
    }

    // Filter by failure reason - supports multiple filter types
    if (selectedFailureFilterKey) {
      // Issue count filters
      if (selectedFailureFilterKey === "issueCount:single") {
        // Items with exactly 1 failure reason
        rows = rows.filter((row) => row.allFailureReasons && row.allFailureReasons.length === 1);
      } else if (selectedFailureFilterKey === "issueCount:multiple") {
        // Items with 2+ failure reasons
        rows = rows.filter((row) => row.allFailureReasons && row.allFailureReasons.length > 1);
      }
      // Has reason filter - items that have this reason (may have others)
      else if (selectedFailureFilterKey.startsWith("hasReason:")) {
        const reason = selectedFailureFilterKey.split(":")[1];
        rows = rows.filter((row) => row.allFailureReasons && row.allFailureReasons.includes(reason));
      }
      // Exact combination filter
      else if (selectedFailureFilterKey.startsWith("combo:")) {
        const targetReasons = selectedFailureFilterKey.split(":")[1].split("+").sort();
        rows = rows.filter((row) => {
          if (!row.allFailureReasons) return false;
          const rowReasons = [...row.allFailureReasons].sort();
          if (rowReasons.length !== targetReasons.length) return false;
          return rowReasons.every((r, i) => r === targetReasons[i]);
        });
      }
      // Legacy single-reason filters (backward compatibility)
      else {
        switch (selectedFailureFilterKey) {
          case "unmappedProduct":
            rows = rows.filter((row) => !row.isSupportedProduct && !row.dcrPresent);
            break;
          case "missingTPID":
            rows = rows.filter((row) => !row.tpid && row.isSupportedProduct && !row.dcrPresent);
            break;
          case "invalidUATState":
            rows = rows.filter((row) => !row.hasValidStatus && row.isSupportedProduct && row.tpid && !row.dcrPresent);
            break;
          case "missingParentFeature":
            rows = rows.filter(
              (row) =>
                !row.isLinkedToValidFeature && row.isSupportedProduct && row.tpid && row.hasValidStatus && !row.dcrPresent
            );
            break;
          case "totalAutomationFailures":
            rows = rows.filter(
              (row) =>
                (!row.isLinkedToValidFeature || !row.hasValidStatus || !row.isSupportedProduct || !row.tpid) &&
                !row.dcrPresent
            );
            break;
          case "nonAutomationFailures":
            rows = rows.filter(
              (row) => row.isLinkedToValidFeature && row.hasValidStatus && row.isSupportedProduct && !row.dcrPresent
            );
            break;
        }
      }
    }

    return rows;
  }, [data?.reconciliationRows, selectedCategories, selectedProducts, selectedFailureFilterKey]);

  const hasActiveFilters =
    selectedCategories.length > 0 ||
    selectedOfferings.length > 0 ||
    selectedProducts.length > 0 ||
    searchTerm.trim().length > 0 ||
    !!selectedFailureFilterKey;

  // Get the display name for selected failure filter
  const selectedFailureDisplayName = useMemo(() => {
    if (!selectedFailureFilterKey || !data?.failureReasons) return "";
    const found = data.failureReasons.find((fr) => fr.filterKey === selectedFailureFilterKey);
    return found?.reason || selectedFailureFilterKey;
  }, [selectedFailureFilterKey, data?.failureReasons]);

  // Convert selectedValues array to selectedKeys object for CustomFilterPill
  const categorySelectedKeys = useMemo(() => {
    if (!selectedCategories || selectedCategories.length === 0) return undefined;
    return selectedCategories.reduce(
      (acc, value) => {
        acc[value] = true;
        return acc;
      },
      {} as { [key: string]: boolean }
    );
  }, [selectedCategories]);

  const productSelectedKeys = useMemo(() => {
    if (!selectedProducts || selectedProducts.length === 0) return undefined;
    return selectedProducts.reduce(
      (acc, value) => {
        acc[value] = true;
        return acc;
      },
      {} as { [key: string]: boolean }
    );
  }, [selectedProducts]);

  const offeringSelectedKeys = useMemo(() => {
    if (!selectedOfferings || selectedOfferings.length === 0) return undefined;
    return selectedOfferings.reduce(
      (acc, value) => {
        acc[value] = true;
        return acc;
      },
      {} as { [key: string]: boolean }
    );
  }, [selectedOfferings]);

  const categoryMenuItems = useMemo(() => mapToMenuItems(data?.categories || []), [data?.categories]);
  const offeringMenuItems = useMemo(() => mapToMenuItems(data?.offerings || []), [data?.offerings]);
  const productMenuItems = useMemo(() => mapToMenuItems(data?.azureServices || []), [data?.azureServices]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "workItemId",
        headerName: "Work Item ID",
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <MuiLink
            href={params.row.caseNumberUrl || "#"}
            target="_blank"
            sx={{
              color: THEME.primary,
              fontWeight: 500,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" }
            }}
            onClick={(e: React.MouseEvent) => {
              if (!params.row.caseNumberUrl) e.preventDefault();
            }}
          >
            {params.value}
          </MuiLink>
        )
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={params.value || ""} arrow>
            <Typography sx={{ fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {params.value}
            </Typography>
          </Tooltip>
        )
      },
      {
        field: "account",
        headerName: "Account",
        width: 180,
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={params.value || ""} arrow>
            <Typography sx={{ fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {params.value}
            </Typography>
          </Tooltip>
        )
      },
      { field: "tpid", headerName: "TPID", width: 100 },
      { field: "offeringName", headerName: "Offering", width: 130 },
      { field: "azServiceName", headerName: "Azure Service", width: 180 },
      {
        field: "tagAppliedDate",
        headerName: "Tag Applied",
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Typography sx={{ fontSize: "13px" }}>{params.value ? formatDisplayDate(params.value) : "-"}</Typography>
        )
      },
      {
        field: "triageCompletedDate",
        headerName: "Triage Completed",
        width: 130,
        renderCell: (params: GridRenderCellParams) => (
          <Typography sx={{ fontSize: "13px" }}>{params.value ? formatDisplayDate(params.value) : "-"}</Typography>
        )
      },
      {
        field: "daysToTriageCompletion",
        headerName: "Days to Triage",
        width: 120,
        type: "number",
        renderCell: (params: GridRenderCellParams) => (
          <Typography sx={{ fontSize: "13px" }}>{params.value != null ? params.value : "-"}</Typography>
        )
      },
      {
        field: "category",
        headerName: "Category",
        width: 200,
        renderCell: (params: GridRenderCellParams) => {
          const colors = CATEGORY_COLORS[params.value] || {
            bg: THEME.borderLight,
            text: THEME.textSecondary,
            border: THEME.border
          };
          return (
            <Chip
              label={params.value}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.text,
                fontWeight: 500,
                fontSize: "11px",
                height: 26,
                borderRadius: "6px"
              }}
            />
          );
        }
      },
      {
        field: "failureReason",
        headerName: "Primary Reason",
        width: 150,
        description: "Primary failure reason (used for filtering)",
        renderCell: (params: GridRenderCellParams) => {
          if (!params.value) return <Typography sx={{ fontSize: "13px", color: THEME.textMuted }}>-</Typography>;
          const failureColors: Record<string, { bg: string; text: string }> = {
            "Unmapped Product": { bg: "#FFF4CE", text: "#8A6D3B" },
            "Missing TPID": { bg: "#FCE8EC", text: "#C4314B" },
            "Invalid UAT State": { bg: "#FDF3EE", text: "#D77440" },
            "Missing Parent Feature": { bg: "#EFF6FC", text: "#0F6CBD" },
            Other: { bg: "#F3F2F1", text: "#605E5C" }
          };
          const colors = failureColors[params.value] || { bg: THEME.borderLight, text: THEME.textSecondary };
          return (
            <Chip
              label={params.value}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.text,
                fontWeight: 500,
                fontSize: "11px",
                height: 26,
                borderRadius: "6px"
              }}
            />
          );
        }
      },
      {
        field: "allFailureReasons",
        headerName: "All Issues",
        minWidth: 260,
        flex: 1,
        description: "All applicable failure reasons (a work item can have multiple issues)",
        sortable: false,
        cellClassName: "all-issues-cell",
        renderCell: (params: GridRenderCellParams) => {
          const reasons = params.value as string[] || [];
          if (reasons.length === 0) return <Typography sx={{ fontSize: "13px", color: THEME.textMuted }}>-</Typography>;

          const failureColors: Record<string, { bg: string; text: string }> = {
            "Unmapped Product": { bg: "#FFF4CE", text: "#8A6D3B" },
            "Missing TPID": { bg: "#FCE8EC", text: "#C4314B" },
            "Invalid UAT State": { bg: "#FDF3EE", text: "#D77440" },
            "Missing Parent Feature": { bg: "#EFF6FC", text: "#0F6CBD" },
            Other: { bg: "#F3F2F1", text: "#605E5C" }
          };

          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, width: "100%", py: 0.75 }}>
              {reasons.map((reason, idx) => {
                const colors = failureColors[reason] || { bg: THEME.borderLight, text: THEME.textSecondary };
                return (
                  <Chip
                    key={idx}
                    label={reason.replace("Missing ", "").replace(" Feature", "")}
                    size="small"
                    sx={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      fontWeight: 500,
                      fontSize: "10px",
                      height: 20,
                      borderRadius: "4px",
                      "& .MuiChip-label": { px: 0.75 }
                    }}
                  />
                );
              })}
              {reasons.length > 1 && (
                <Tooltip title="This item has multiple failure reasons" arrow>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 20,
                      height: 20,
                      borderRadius: "4px",
                      backgroundColor: THEME.error,
                      color: "white",
                      fontSize: "10px",
                      fontWeight: 700
                    }}
                  >
                    {reasons.length}
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        }
      },
      {
        field: "dcrRequestId",
        headerName: "DCR ID",
        width: 130,
        renderCell: (params: GridRenderCellParams) => {
          if (!params.value) return <Typography sx={{ fontSize: "13px", color: THEME.textMuted }}>-</Typography>;
          return (
            <MuiLink
              href={params.row.linkToDCRRequest || "#"}
              target="_blank"
              sx={{
                color: THEME.primary,
                fontWeight: 500,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" }
              }}
              onClick={(e: React.MouseEvent) => {
                if (!params.row.linkToDCRRequest) e.preventDefault();
              }}
            >
              {params.value}
            </MuiLink>
          );
        }
      }
    ],
    []
  );

  const handleClearFilters = useCallback(() => {
    setSelectedOfferings([]);
    setSelectedProducts([]);
    setSelectedCategories([]);
    setSelectedFailureFilterKey(undefined);
    setSearchTerm("");
  }, []);

  const handleCategoryChange = useCallback((values: string[] | undefined) => {
    setSelectedCategories(values || []);
  }, []);

  const handleOfferingChange = useCallback((values: string[] | undefined) => {
    setSelectedOfferings(values || []);
  }, []);

  const handleProductChange = useCallback((values: string[] | undefined) => {
    setSelectedProducts(values || []);
  }, []);

  // Rich refresh indicator: shown while re-fetching after a filter change (but not on first load)
  const isRefreshing = isFetching && !isLoading;

  if (error) {
    return (
      <MessageBar messageBarType={MessageBarType.error}>
        Failed to load DCR Automation data. Please try again later.
      </MessageBar>
    );
  }

  return (
    <Box sx={{ position: "relative", pb: 4 }}>
      {/* Keyframes for the refresh spinner icon */}
      <style>{`@keyframes ctl-itt-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Top progress bar shown whenever data is refreshing */}
      <Fade in={isRefreshing} unmountOnExit>
        <LinearProgress
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            height: 3,
            borderRadius: "3px",
            mb: 1,
            backgroundColor: alpha(THEME.primary, 0.12),
            "& .MuiLinearProgress-bar": { backgroundColor: THEME.primary }
          }}
        />
      </Fade>

      {/* Info Banner */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2.5,
          borderRadius: "10px",
          background: `linear-gradient(135deg, ${THEME.primaryLight} 0%, ${alpha(THEME.primary, 0.05)} 100%)`,
          border: `1px solid ${THEME.primaryMedium}`
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Info16Regular style={{ color: THEME.primary, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "12px", color: THEME.textPrimary, lineHeight: 1.5 }}>
            <strong>Data Note:</strong> This report highlights the current outcome of UAT-to-CRM DCR automation,
            including successful creation, pending processing, and conversion failures with associated reasons.
          </Typography>
        </Box>
      </Paper>

      {/* Top Filter - Offering only (applies to all charts) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2.5,
          p: 1.5,
          borderRadius: "10px",
          background: `linear-gradient(135deg, ${THEME.background} 0%, ${alpha(THEME.primary, 0.03)} 100%)`,
          border: `1px solid ${THEME.border}`
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: THEME.textPrimary,
            whiteSpace: "nowrap"
          }}
        >
          Filter by Offering:
        </Typography>
        {(data?.offerings?.length || 0) > 0 && (
          <CustomFilterPill
            defaultValue="All Offerings"
            isRemovable={true}
            name="Offering"
            styles={FILTER_PILL_STYLES}
            selectedKeys={offeringSelectedKeys}
            useGroupedFilters={false}
            menuProps={{
              focusZoneProps: {
                shouldInputLoseFocusOnArrowKey: () => true
              },
              items: offeringMenuItems
            }}
            onApply={handleOfferingChange}
            onCancel={handleOfferingChange}
            onResetFilters={() => handleOfferingChange([])}
            selectAllButton={{
              show: true,
              label: "Select All",
              selectValues: data?.offerings || []
            }}
          />
        )}
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: THEME.textPrimary,
            whiteSpace: "nowrap"
          }}
        >
          Filter by Product:
        </Typography>
        {(data?.azureServices?.length || 0) > 0 && (
          <CustomFilterPill
            defaultValue="All Products"
            isRemovable={true}
            name="Product"
            styles={FILTER_PILL_STYLES}
            selectedKeys={productSelectedKeys}
            useGroupedFilters={false}
            menuProps={{
              focusZoneProps: {
                shouldInputLoseFocusOnArrowKey: () => true
              },
              items: productMenuItems
            }}
            onApply={handleProductChange}
            onCancel={handleProductChange}
            onResetFilters={() => handleProductChange([])}
            selectAllButton={{
              show: true,
              label: "Select All",
              selectValues: data?.azureServices || []
            }}
          />
        )}
        <Typography
          sx={{
            fontSize: "11px",
            color: THEME.textMuted,
            fontStyle: "italic"
          }}
        >
          (Filters all charts and data)
        </Typography>
        {/* Refreshing indicator pill */}
        <Fade in={isRefreshing} unmountOnExit>
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: "16px",
              backgroundColor: THEME.primaryLight,
              border: `1px solid ${alpha(THEME.primary, 0.2)}`
            }}
          >
            <ArrowClockwise16Regular style={{ color: THEME.primary, animation: "ctl-itt-spin 0.8s linear infinite" }} />
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.primary }}>Updating results…</Typography>
          </Box>
        </Fade>
      </Box>

      {/* Content sections - subtly dimmed while refreshing so the change is noticeable */}
      <Box
        sx={{
          transition: "opacity 0.25s ease, filter 0.25s ease",
          opacity: isRefreshing ? 0.55 : 1,
          filter: isRefreshing ? "grayscale(0.15)" : "none",
          pointerEvents: isRefreshing ? "none" : "auto"
        }}
      >
        {/* Summary Cards - Grid layout */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
            gap: 2,
            mb: 3
          }}
        >
          <SummaryCard
            title="UAT Feedback Workitems"
            value={data?.summary.totalWorkItemsSubmitted || 0}
            subtitle="Total unique workitems submitted"
            accentColor={THEME.primary}
            isLoading={isLoading}
            tooltip={
              <CTLInfoTooltip
                title="UAT Feedback Workitems"
                description="The starting point of the intake journey — every piece of customer feedback the field logged and flagged for a formal customer request to be raised."
                howItsCalculated="We count each unique feedback work item once, no matter how many times it was touched or retried."
                note="This is the baseline that all the other numbers on this row are measured against."
              />
            }
          />
          <SummaryCard
            title="Successfully Created (First Attempt)"
            value={data?.summary.dcrSuccessfullyCreatedFirstAttempt || 0}
            subtitle="DCR created within 3 days"
            accentColor={THEME.success}
            isLoading={isLoading}
            tooltip={
              <CTLInfoTooltip
                title="Successfully Created (First Attempt)"
                description="Automation working as intended — feedback that flowed straight through into a formal customer request without anyone having to step in and fix it."
                howItsCalculated="We count the requests that were created within 3 days of the feedback being flagged. Anything slower is treated as a retry rather than a clean first pass."
                note="The higher this is against the total on the left, the healthier the automation."
              />
            }
          />
          <SummaryCard
            title="Not Converted to DCR"
            value={data?.summary.workItemsNotConvertedToDCRs || 0}
            subtitle={`${data?.summary.totalWorkItemsSubmitted ? Math.round((data.summary.workItemsNotConvertedToDCRs / data.summary.totalWorkItemsSubmitted) * 100) : 0}% of actionable items`}
            accentColor={THEME.error}
            isLoading={isLoading}
            tooltip={
              <CTLInfoTooltip
                title="Not Converted to DCR"
                description="Customer feedback that never became a formal request — effectively lost customer voice. These are the items worth chasing."
                howItsCalculated="We count the feedback work items that still have no customer request against them today. The percentage is these items as a share of all feedback work items submitted."
                note="The 'Failure Reasons Analysis' chart below explains why these items did not make it through."
              />
            }
          />
          <SummaryCard
            title="Successfully Created (After Retry)"
            value={data?.summary.dcrSuccessfullyCreatedOnRetry || 0}
            subtitle="Initially failed, recovered"
            accentColor={THEME.purple}
            isLoading={isLoading}
            tooltip={
              <CTLInfoTooltip
                title="Successfully Created (After Retry)"
                description="Feedback that eventually made it through, but only after the first attempt failed and something had to be corrected or re-run. The customer waited longer than they should have."
                howItsCalculated="We count the requests that were created more than 3 days after the feedback was flagged, which means the initial automated attempt did not succeed."
                note="A large number here points to data quality problems that are recoverable but costly in time."
              />
            }
          />
          <SummaryCard
            title="DCR Pending Triage"
            value={data?.summary.dcrPendingTriage || 0}
            subtitle="Tagged ≤3 days ago, awaiting sync"
            accentColor={THEME.warning}
            isLoading={isLoading}
            tooltip={
              <CTLInfoTooltip
                title="DCR Pending Triage"
                description="Requests that have been created successfully but are still sitting in the queue waiting for the CxE team to review them. This is work in hand, not a failure."
                howItsCalculated="We count the requests whose triage status is still 'CxE Triage Needed' — created, but not yet picked up for review."
                note="Expect a small, steady number here. A growing number suggests triage capacity is not keeping up with intake."
              />
            }
          />
        </Box>

        {/* Charts Row 1 - Side by side */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: 2,
            mb: 2
          }}
        >
          <FailureReasonsChart
            data={data?.failureReasons || []}
            isLoading={isLoading}
            selectedFilterKey={selectedFailureFilterKey}
            onReasonClick={setSelectedFailureFilterKey}
            filteredCount={filteredRows.length}
            itemsWithMultipleIssues={data?.itemsWithMultipleIssues || 0}
            failureOverlapAnalysis={data?.failureOverlapAnalysis}
          />
          <RecoveryTimeChart data={data?.recoveryTimeBuckets || []} isLoading={isLoading} />
        </Box>

        {/* Charts Row 2 - Side by side equal size */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: 2,
            mb: 2
          }}
        >
          <DCRCreationByProductChart
            data={data?.creationByProduct || []}
            selectedOfferings={selectedOfferings}
            isLoading={isLoading}
          />
          <ConversionByOfferingChart
            data={data?.conversionByOffering || []}
            selectedOfferings={selectedOfferings}
            isLoading={isLoading}
          />
        </Box>

        {/* Detailed Table */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: "12px",
            border: `1px solid ${THEME.border}`,
            backgroundColor: THEME.surface,
            overflow: "hidden",
            "& *": {
              scrollbarWidth: "none",
              msOverflowStyle: "none"
            },
            "& *::-webkit-scrollbar:horizontal": {
              display: "none",
              height: 0
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              pb: 1.5,
              borderBottom: `1px solid ${THEME.borderLight}`
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography sx={{ fontSize: "15px", fontWeight: 600, color: THEME.textPrimary }}>
                Detailed Reconciliation Table
              </Typography>
              <CTLInfoTooltip
                title="Detailed Reconciliation Table"
                description="The item-by-item audit trail behind every chart above. Use it to see exactly which pieces of customer feedback converted, which are stuck, and what is holding each one up."
                howItsCalculated="Each row is one piece of UAT feedback matched against the customer request it produced, together with the account, product and the specific issues that blocked or delayed it."
                note="Clicking a segment in the charts above filters this table, and the item count updates to match."
              />
            </Box>
            <Typography sx={{ fontSize: "13px", color: THEME.textMuted }}>
              {filteredRows.length.toLocaleString()} items
            </Typography>
          </Box>

          {/* Table Filters - Category and Search */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: THEME.background,
              border: `1px solid ${THEME.borderLight}`,
              flexWrap: "wrap"
            }}
          >
            {(data?.categories?.length || 0) > 0 && (
              <CustomFilterPill
                defaultValue="All"
                isRemovable={true}
                name="Category"
                styles={FILTER_PILL_STYLES}
                selectedKeys={categorySelectedKeys}
                useGroupedFilters={false}
                menuProps={{
                  focusZoneProps: {
                    shouldInputLoseFocusOnArrowKey: () => true
                  },
                  items: categoryMenuItems
                }}
                onApply={handleCategoryChange}
                onCancel={handleCategoryChange}
                onResetFilters={() => handleCategoryChange([])}
                selectAllButton={{
                  show: true,
                  label: "Select All",
                  selectValues: data?.categories || []
                }}
              />
            )}
            <Box sx={{ flex: 1, minWidth: 250, maxWidth: 350 }}>
              <SearchBox
                placeholder="Search title, account, TPID, DCR ID..."
                value={searchTerm}
                onChange={(_, value) => setSearchTerm(value || "")}
                styles={{
                  root: {
                    width: "100%",
                    height: 34,
                    borderRadius: "6px"
                  },
                  field: {
                    backgroundColor: THEME.surface,
                    fontSize: "12px"
                  },
                  icon: { color: THEME.textMuted }
                }}
              />
            </Box>
            {/* Active Failure Reason Filter Chip */}
            {selectedFailureFilterKey && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "16px",
                  background: `linear-gradient(135deg, ${alpha(THEME.warning, 0.15)} 0%, ${alpha(THEME.warning, 0.08)} 100%)`,
                  border: `1px solid ${alpha(THEME.warning, 0.4)}`,
                  boxShadow: `0 2px 4px ${alpha(THEME.warning, 0.15)}`
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "2px",
                    backgroundColor: THEME.warning
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: THEME.textPrimary
                  }}
                >
                  Failure: {selectedFailureDisplayName}
                </Typography>
                <Box
                  component="button"
                  onClick={() => setSelectedFailureFilterKey(undefined)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 16,
                    height: 16,
                    p: 0,
                    border: "none",
                    borderRadius: "50%",
                    backgroundColor: alpha(THEME.warning, 0.3),
                    color: THEME.textPrimary,
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 700,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: THEME.warning,
                      color: "white"
                    }
                  }}
                >
                  ×
                </Box>
              </Box>
            )}
            {(selectedCategories.length > 0 || searchTerm.trim().length > 0 || selectedFailureFilterKey) && (
              <Box
                component="button"
                onClick={() => {
                  setSelectedCategories([]);
                  setSelectedFailureFilterKey(undefined);
                  setSearchTerm("");
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.75,
                  border: `1px solid ${alpha(THEME.error, 0.3)}`,
                  borderRadius: "6px",
                  backgroundColor: alpha(THEME.error, 0.05),
                  color: THEME.error,
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: alpha(THEME.error, 0.1),
                    borderColor: THEME.error
                  }
                }}
              >
                Clear Filters
              </Box>
            )}
          </Box>

          {/* DataGrid */}
          <Box
            sx={{
              height: 500,
              overflow: "hidden",
              "& .MuiDataGrid-root": {
                border: "none",
                fontSize: "13px",
                overflow: "auto"
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: THEME.primary,
                color: "white",
                borderRadius: 0
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                fontSize: "12px"
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${THEME.borderLight}`,
                fontSize: "12px",
                py: 1
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: alpha(THEME.primary, 0.04)
              },
              "& .MuiDataGrid-menuIconButton": { color: "white" },
              "& .MuiDataGrid-sortIcon": { color: "white" },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${THEME.border}`,
                minHeight: 48,
                overflow: "hidden"
              },
              "& .MuiDataGrid-toolbarContainer": {
                padding: "8px 12px",
                borderBottom: `1px solid ${THEME.borderLight}`
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "auto"
              },
              "& .MuiTablePagination-toolbar": {
                overflow: "hidden"
              },
              "& .MuiTablePagination-root": {
                overflow: "hidden"
              }
            }}
          >
            <DataGrid
              rows={filteredRows.map((row, index) => ({ ...row, id: row.workItemId || index }))}
              columns={columns}
              loading={isLoading}
              getRowHeight={() => "auto"}
              getEstimatedRowHeight={() => 72}
              filterModel={filterModel}
              onFilterModelChange={setFilterModel}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } }
              }}
              slots={{
                toolbar: () => (
                  <CustomDataGridToolbar
                    filterModel={filterModel}
                    toolbarConfig={{ disableGridToolbarQuickFilter: false }}
                    exportCsvButtonProps={{
                      buttonLabel: "Export CSV",
                      fileNamePrefix: "dcr-reconciliation",
                      tooltip: "Export data to CSV"
                    }}
                  />
                )
              }}
              disableRowSelectionOnClick
              density="compact"
              sx={{
                "& .MuiDataGrid-main": {
                  overflowX: "auto"
                },
                "& .MuiDataGrid-scrollbar--horizontal": {
                  display: "block"
                },
                "& .all-issues-cell": {
                  alignItems: "flex-start",
                  py: 0.5
                },
                "& .all-issues-cell.MuiDataGrid-cell": {
                  overflow: "visible"
                }
              }}
            />
          </Box>

          {/* Explore Data Button Footer - Matches CustomCard footer style */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              minHeight: 45,
              borderTop: `2px solid ${THEME.borderLight}`,
              mt: 1,
              pt: 1
            }}
          >
            <CTLExploreDataButton
              query={CTL_DCR_AUTOMATION_QUERY}
              chartTitle="Detailed Reconciliation Table"
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default IntakeToTriage;
